import { Card, Typography, Form, Input, InputNumber, Select, Button, Row, Col, Space, Tag, Divider, Switch, Upload } from 'antd'
import React, { useEffect, useState } from 'react'
import {
    ArrowLeftOutlined, InfoCircleOutlined, DollarOutlined, DatabaseOutlined,
    PaperClipOutlined, InboxOutlined, FileTextOutlined, BulbOutlined, LinkOutlined,
    BarcodeOutlined,
} from "@ant-design/icons"
import { ItemService } from '../../../Services/Manage/ItemService'
import { ENDPOINT } from '../../../Services/ENDPOINT'
import useMessage from 'antd/es/message/useMessage'
import { useLocation, useNavigate } from 'react-router-dom'

const { TextArea } = Input;
const { Title, Text } = Typography;

const compactFormStyle = `
  .ant-form-item { margin-bottom: 15px !important; }
  .ant-card-body { padding: 16px 16px !important; }
  .ant-card { margin-bottom: 16px !important; }
  .ant-form-item-label { padding-bottom: 2px !important; }
  .ant-form-item-label > label { height: 20px !important; }
`;

const CardTitle = ({ icon, no, text }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "rgba(14, 93, 146, 0.95)" }}>
        {icon}
        <span style={{ fontWeight: 600 }}>{no}. {text}</span>
    </span>
);

const taxTypeOptions = [
    { value: "Inclusive", label: "Inclusive" },
    { value: "Exclusive", label: "Exclusive" },
];

const quickLinks = [
    { label: "View Products / Items", to: "/inventory/products-items" },
    { label: "Manage Categories", to: "/inventory/categories" },
    { label: "Manage Suppliers", to: "/inventory/suppliers" },
    { label: "Purchase Orders", to: "/inventory/purchase-orders" },
    { label: "Goods Receipts", to: "/inventory/goods-receipts" },
];

const AddItem = () => {
    const [messageApi, msgContext] = useMessage();
    const [form] = Form.useForm();
    const [itemCode, setItemCode] = useState("ITM-001");
    const [itemTypeOptions, setItemTypeOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [unitOptions, setUnitOptions] = useState([]);
    const [brandOptions, setBrandOptions] = useState([]);
    const [priceListOptions, setPriceListOptions] = useState([]);
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [taxRateOptions, setTaxRateOptions] = useState([]);

    const [editItem, setEditItem] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [itemImage, setItemImage] = useState(null);
    const [pendingItemImage, setPendingItemImage] = useState(null);
    const [additionalImages, setAdditionalImages] = useState([]);
    const [pendingAdditionalImages, setPendingAdditionalImages] = useState([]);
    const [saving, setSaving] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem("ome_user")) || {}; } catch { return {}; }
    })();
    const currentUserId = currentUser.uid;
    const id = location.state?.id;
    const action = location.state?.action;
    const enableEdit = location.state?.enableEdit;
    const from = location.state?.from || '/inventory';
    const isReadOnly = !enableEdit && !!editItem;

    // Live values for the "Item Summary" sidebar preview.
    const summary = Form.useWatch([], form) || {};
    const firstOf = (v) => (Array.isArray(v) ? v[0] : v);

    useEffect(() => {
        getConfigOptions();
        if (id) {
            getItemById(id);
        } else {
            getLastItem();
        }
    }, [id]);

    const getConfigOptions = async () => {
        try {
            const fields = ["item_type", "category", "unit", "brand", "price_list", "tax_rate", "warehouse"];
            const results = await Promise.all(fields.map((f) => ItemService.getDistinctValues(f)));
            const toOptions = (arr) => (arr || []).map((v) => ({ value: v, label: v }));

            setItemTypeOptions(toOptions(results[0].data));
            setCategoryOptions(toOptions(results[1].data));
            setUnitOptions(toOptions(results[2].data));
            setBrandOptions(toOptions(results[3].data));
            setPriceListOptions(toOptions(results[4].data));
            setTaxRateOptions(toOptions(results[5].data));
            setWarehouseOptions(toOptions(results[6].data));
        } catch (error) {
            console.error(error);
        }
    };


    const getLastItem = async () => {
        try {
            const request = await ItemService.getLastItem();
            let nextNo = "001";
            if (request?.status && request?.data?.item_code) {
                const seqPart = request.data.item_code.split("-").pop();
                const parsed = parseInt(seqPart, 10);
                if (!isNaN(parsed)) {
                    nextNo = String(parsed + 1).padStart(3, "0");
                }
            }
            setItemCode(`ITM-${nextNo}`);
        } catch (error) {
            console.error(error);
        }
    };

    const getItemById = async (id) => {
        try {
            const request = await ItemService.getItemById(id);
            const initialValues = request.data;
            setEditItem(initialValues);

            setItemCode(initialValues.item_code || "ITM-001");

            let tags = initialValues.tags;
            if (typeof tags === 'string') {
                try { tags = JSON.parse(tags); } catch { tags = []; }
            }

            let additionalImagesData = initialValues.additional_images;
            if (typeof additionalImagesData === 'string') {
                try { additionalImagesData = JSON.parse(additionalImagesData); } catch { additionalImagesData = []; }
            }
            setAdditionalImages(additionalImagesData || []);

            let attachments = initialValues.attachments;
            if (typeof attachments === 'string') {
                try { attachments = JSON.parse(attachments); } catch { attachments = []; }
            }

            setFileList((attachments || []).map((f, idx) => ({
                uid: `existing-${idx}`,
                name: f.filename || f.name,
                status: 'done',
                url: f.path?.startsWith('http') ? f.path : `${ENDPOINT.base}/${f.path}`,
                response: { file: f },
            })));

            setItemImage(initialValues.item_image || null);

            form.setFieldsValue({
                ...initialValues,
                item_type: initialValues.item_type ? [initialValues.item_type] : [],
                category: initialValues.category ? [initialValues.category] : [],
                unit: initialValues.unit ? [initialValues.unit] : [],
                brand: initialValues.brand ? [initialValues.brand] : [],
                price_list: initialValues.price_list ? [initialValues.price_list] : [],
                tax_rate: initialValues.tax_rate ? [initialValues.tax_rate] : [],
                warehouse: initialValues.warehouse ? [initialValues.warehouse] : [],
                tags: tags || [],
            });
        } catch (error) {
            console.error(error);
        }
    };

    const uploadOneFile = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch(`${ENDPOINT.Item}/upload-file`, { method: "POST", body: formData });
        const result = await res.json();
        return result?.status && result?.success ? result.file : null;
    };

    const onFinish = async (values) => {
        await performSave(values);
    };

    const handleSaveDraft = async () => {
        const values = form.getFieldsValue();
        await performSave(values);
    };

    const performSave = async (values) => {
        setSaving(true);
        try {
            let itemImagePath = itemImage;
            if (pendingItemImage) {
                const uploaded = await uploadOneFile(pendingItemImage.originFileObj);
                if (uploaded) itemImagePath = uploaded.path;
            }

            let uploadedAttachments = [];
            for (const pf of pendingFiles) {
                const uploaded = await uploadOneFile(pf.originFileObj);
                if (uploaded) uploadedAttachments.push(uploaded);
            }

            let uploadedAdditionalImages = [];
            for (const pf of pendingAdditionalImages) {
                const uploaded = await uploadOneFile(pf.originFileObj);
                if (uploaded) uploadedAdditionalImages.push(uploaded);
            }

            const existingAttachments = fileList
                .filter((f) => !f.__pending)
                .map((f) => f.response?.file)
                .filter(Boolean);

            const attachments = [...existingAttachments, ...uploadedAttachments];
            const allAdditionalImages = [...additionalImages, ...uploadedAdditionalImages];

            const payload = {
                ...values,
                item_type: firstOf(values.item_type),
                category: firstOf(values.category),
                unit: firstOf(values.unit),
                brand: firstOf(values.brand),
                price_list: firstOf(values.price_list),
                tax_rate: firstOf(values.tax_rate),
                warehouse: firstOf(values.warehouse),
                current_stock: values.current_stock,
                available_stock: values.current_stock,
                item_image: itemImagePath,
                added_by: editItem ? editItem.added_by : currentUserId,
                tags: JSON.stringify(values.tags || []),
                attachments: JSON.stringify(attachments),
                additional_images: JSON.stringify(allAdditionalImages),

                item_code: itemCode,
            };

            let request;
            if (editItem) {
                request = await ItemService.updateItem(payload, id);
            } else {
                request = await ItemService.createItem(payload);
            }

            if (request?.status) {
                messageApi.success(request.message);
                if (!editItem) {
                    form.resetFields();
                    setFileList([]);
                    setPendingFiles([]);
                    setItemImage(null);
                    setPendingItemImage(null);
                    setAdditionalImages([]);
                    setPendingAdditionalImages([]);
                }
                navigate(from);
            } else {
                messageApi.error(request?.message || "Something went wrong");
            }
        } catch (error) {
            console.error("Error in onFinish:", error);
            messageApi.error("Unexpected error occurred");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            const request = await ItemService.deleteItem(id);
            if (request.status) {
                messageApi.success(request.message);
                navigate(from);
            } else {
                messageApi.error(request.message);
            }
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <>
            <style>{compactFormStyle}</style>
            {msgContext}
            <Card
                title={
                    <Space align="center">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <ArrowLeftOutlined
                                style={{ cursor: "pointer", fontSize: "20px" }}
                                onClick={() => navigate(from)}
                            />
                            <Title level={4} style={{ margin: 0 }}>
                                <Space>
                                    {editItem ? `${action} Item` : 'Add Item'}{' '}
                                    {editItem && (
                                        <Tag color="cyan" style={{ fontSize: '16px', padding: '6px 16px', borderRadius: '8px', fontWeight: 600 }}>
                                            SKU: {editItem.sku_barcode}{editItem.barcode ? ` | Barcode: ${editItem.barcode}` : ""}
                                        </Tag>
                                    )}
                                </Space>
                            </Title>
                        </div>
                    </Space>
                }
                extra={
                    action === 'View' ? null : !enableEdit && editItem ? (
                        <Button size="large" danger onClick={() => navigate("/inventory/edit-item", { state: { id, action: 'Edit', enableEdit: true, from } })}>
                            Enable Edit
                        </Button>
                    ) : (
                        <Space>
                            <Button size="large" loading={saving} onClick={handleSaveDraft}>Save as Draft</Button>
                            <Button
                                type="primary" size="large" loading={saving} onClick={() => form.submit()}
                                style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                            >
                                {editItem ? "Update Item" : "Save Item"}
                            </Button>
                        </Space>
                    )
                }
            >
                <Row gutter={16}>
                    <Col xs={24} lg={17}>
                        <Form
                            layout="vertical"
                            form={form}
                            onFinish={onFinish}
                            size="middle"
                            disabled={isReadOnly}
                            initialValues={{ tax_type: 'Exclusive', track_inventory: true, is_active: true }}
                        >
                            <Card title={<CardTitle icon={<InfoCircleOutlined />} no={1} text="Basic Information" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="item_type" label="Item Type" rules={[{ required: true, message: 'Please select item type' }]}>
                                            <Select options={itemTypeOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type item type" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="item_name" label="Item Name" rules={[{ required: true, message: 'Please enter item name' }]}>
                                            <Input size="large" placeholder="Enter item name" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="sku_barcode" label="SKU Code" rules={[{ required: true, message: 'Please enter SKU code' }]}>
                                            <Input size="large" placeholder="Enter SKU code" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="barcode" label="Barcode">
                                            <Input
                                                size="large"
                                                placeholder="Enter or generate barcode"
                                                suffix={
                                                    <BarcodeOutlined
                                                        style={{ cursor: 'pointer', color: '#888', fontSize: 16 }}
                                                        title="Generate barcode number"
                                                        onClick={() => {
                                                            const generated = Date.now().toString().slice(-12);
                                                            form.setFieldsValue({ barcode: generated });
                                                        }}
                                                    />
                                                }
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="category" label="Category" rules={[{ required: true, message: 'Please select category' }]}>
                                            <Select options={categoryOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type category" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="unit" label="Unit" rules={[{ required: true, message: 'Please select unit' }]}>
                                            <Select options={unitOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type unit" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="brand" label="Brand">
                                            <Select options={brandOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type brand (optional)" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="Item Code">
                                            <Input size="large" readOnly value={itemCode} placeholder="Auto Generate" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="description" label="Description">
                                            <TextArea rows={3} size="large" maxLength={500} showCount placeholder="Enter item description..." />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle icon={<DollarOutlined />} no={2} text="Pricing & Taxation" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="cost_price" label="Cost Price (₹)" rules={[{ required: true, message: 'Please enter cost price' }]}>
                                            <InputNumber size="large" min={0} style={{ width: '100%' }} placeholder="Enter cost price" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="selling_price" label="Selling Price (₹)" rules={[{ required: true, message: 'Please enter selling price' }]}>
                                            <InputNumber size="large" min={0} style={{ width: '100%' }} placeholder="Enter selling price" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="mrp" label="MRP (₹)">
                                            <InputNumber size="large" min={0} style={{ width: '100%' }} placeholder="Enter MRP" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="tax_rate" label="Tax Rate (%)">
                                            <Select options={taxRateOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type tax rate" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="hsn_sac_code" label="HSN / SAC Code">
                                            <Input size="large" placeholder="Enter HSN / SAC code" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="tax_type" label="Tax Type">
                                            <Select options={taxTypeOptions} size="large" placeholder="Select tax type" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="price_list" label="Price List">
                                            <Select options={priceListOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type price list (optional)" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle icon={<DatabaseOutlined />} no={3} text="Stock Information" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="opening_stock" label="Opening Stock">
                                            <InputNumber size="large" min={0} style={{ width: '100%' }} placeholder="0" />
                                        </Form.Item>
                                    </Col>
                                    {/* <Col xs={24} sm={12} md={6}>
                                            <Form.Item name="current_stock" label="Current Stock" tooltip="Same value is used as Available Stock" >
                                                <InputNumber size="large" min={0} style={{ width: '100%' }} placeholder="0" />
                                            </Form.Item>
                                        </Col> */}
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="minimum_stock" label="Minimum Stock">
                                            <InputNumber size="large" min={0} style={{ width: '100%' }} placeholder="0" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="reorder_level" label="Reorder Level">
                                            <InputNumber size="large" min={0} style={{ width: '100%' }} placeholder="0" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="maximum_stock" label="Maximum Stock" rules={[{ required: true, message: 'Please enter maximum stock' }]}>
                                            <InputNumber size="large" min={0} style={{ width: '100%' }} placeholder="0" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="warehouse" label="Warehouse" rules={[{ required: true, message: 'Please select warehouse' }]}>
                                            <Select options={warehouseOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type warehouse" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="shelf_location" label="Shelf / Location">
                                            <Input size="large" placeholder="Enter shelf / location (optional)" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={12}>
                                        <Form.Item name="track_inventory" label="Track Inventory" valuePropName="checked">
                                            <Switch />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle icon={<PaperClipOutlined />} no={4} text="Additional Information" />}>
                                <Row gutter={16}>
                                    {/* Row 1 — 4 toggle switches, matches mockup exactly */}
                                    <Col xs={24} sm={6}>
                                        <Form.Item name="serial_number_tracking" label="Serial Number Tracking" valuePropName="checked">
                                            <Switch />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={6}>
                                        <Form.Item name="batch_tracking" label="Batch Tracking" valuePropName="checked">
                                            <Switch />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={6}>
                                        <Form.Item name="expiry_date_tracking" label="Expiry Date Tracking" valuePropName="checked">
                                            <Switch />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={6}>
                                        <Form.Item name="is_active" label="Item is Active" valuePropName="checked">
                                            <Switch />
                                        </Form.Item>
                                    </Col>

                                    {/* Row 2 — Item Image / Additional Images / Attachments, 3 equal columns */}
                                    <Col xs={24} md={8}>
                                        <Form.Item label="Item Image">
                                            <Upload.Dragger
                                                accept=".png,.jpg,.jpeg"
                                                maxCount={1}
                                                fileList={itemImage && !pendingItemImage ? [{ uid: '-1', name: 'item-image', status: 'done', url: `${ENDPOINT.base}/${itemImage}` }] : (pendingItemImage ? [pendingItemImage] : [])}
                                                beforeUpload={(file) => {
                                                    setPendingItemImage({ uid: `pending-img-${Date.now()}`, name: file.name, status: "done", originFileObj: file });
                                                    return false;
                                                }}
                                                onRemove={() => { setPendingItemImage(null); setItemImage(null); }}
                                            >
                                                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                                <p className="ant-upload-text">Click to upload or drag and drop</p>
                                                <p className="ant-upload-hint">PNG, JPG, JPEG (Max. 2MB)</p>
                                            </Upload.Dragger>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={8}>
                                        <Form.Item label="Additional Images">
                                            <Upload.Dragger
                                                accept=".png,.jpg,.jpeg"
                                                multiple
                                                maxCount={5}
                                                fileList={[
                                                    ...additionalImages.map((f, idx) => ({
                                                        uid: `existing-img-${idx}`,
                                                        name: f.filename || f.name,
                                                        status: 'done',
                                                        url: f.path?.startsWith('http') ? f.path : `${ENDPOINT.base}/${f.path}`,
                                                        __existingImg: f,
                                                    })),
                                                    ...pendingAdditionalImages,
                                                ]}
                                                beforeUpload={(file) => {
                                                    setPendingAdditionalImages((prev) => [
                                                        ...prev,
                                                        { uid: `pending-add-img-${Date.now()}-${file.name}`, name: file.name, status: "done", originFileObj: file },
                                                    ]);
                                                    return false;
                                                }}
                                                onRemove={(file) => {
                                                    if (file.__existingImg) {
                                                        setAdditionalImages((prev) => prev.filter((f) => f !== file.__existingImg));
                                                    } else {
                                                        setPendingAdditionalImages((prev) => prev.filter((f) => f.uid !== file.uid));
                                                    }
                                                }}
                                            >
                                                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                                <p className="ant-upload-text">Add Images</p>
                                                <p className="ant-upload-hint">(Max. 5 Images)</p>
                                            </Upload.Dragger>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={8}>
                                        <Form.Item label="Attachments">
                                            <Upload.Dragger
                                                fileList={[...fileList, ...pendingFiles]}
                                                multiple
                                                accept=".pdf,.doc,.docx,.xls,.xlsx"
                                                beforeUpload={(file) => {
                                                    setPendingFiles((prev) => [
                                                        ...prev,
                                                        { uid: `pending-${Date.now()}-${file.name}`, name: file.name, status: "done", originFileObj: file, __pending: true },
                                                    ]);
                                                    return false;
                                                }}
                                                onRemove={(file) => {
                                                    if (file.__pending) {
                                                        setPendingFiles((prev) => prev.filter((f) => f.uid !== file.uid));
                                                    } else {
                                                        setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
                                                    }
                                                }}
                                            >
                                                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                                <p className="ant-upload-text">Click to upload or drag and drop</p>
                                                <p className="ant-upload-hint">PDF, DOC, DOCX, XLS, XLSX (Max. 10MB)</p>
                                            </Upload.Dragger>
                                        </Form.Item>
                                    </Col>

                                    {/* Row 3 — Tags / Manufacturer / Country of Origin / Warranty Period, 4 equal columns */}
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="tags" label="Tags / Keywords">
                                            <Select mode="tags" size="large" placeholder="Enter tags and press enter" open={false} suffixIcon={null} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="manufacturer" label="Manufacturer">
                                            <Input size="large" placeholder="Enter manufacturer (optional)" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="country_of_origin" label="Country of Origin">
                                            <Input size="large" placeholder="Enter country (optional)" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="warranty_period" label="Warranty Period">
                                            <Input size="large" placeholder="Enter warranty period (optional)" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Form>

                        {!isReadOnly && (
                            <div style={{ display: 'flex', justifyContent: 'right', marginTop: 8 }}>
                                <Space>
                                    <Button size='large' onClick={() => form.resetFields()}>Reset</Button>
                                    {editItem && (
                                        <Button size="large" danger onClick={handleDelete}>Delete Item</Button>
                                    )}
                                    <Button
                                        type="primary"
                                        size="large"
                                        loading={saving}
                                        onClick={() => form.submit()}
                                        style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                                    >
                                        {editItem ? "Update Item" : "Save Item"}
                                    </Button>
                                </Space>
                            </div>
                        )}
                    </Col>

                    {/* ---- Right sidebar: Item Summary / Helpful Tips / Quick Links (matches mockup) ---- */}
                    <Col xs={24} lg={7}>
                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}><FileTextOutlined /> Item Summary</span>}
                            size="small"
                        >
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {[
                                    ["Item Name", summary.item_name || "-"],
                                    ["SKU Code", summary.sku_barcode || "-"],
                                    ["Barcode", summary.barcode || "-"],
                                    ["Category", firstOf(summary.category) || "-"],
                                    ["Unit", firstOf(summary.unit) || "-"],
                                    ["Selling Price", summary.selling_price ? `₹${summary.selling_price}` : "-"],
                                    ["Cost Price", summary.cost_price ? `₹${summary.cost_price}` : "-"],
                                    ["Current Stock", summary.current_stock ?? "-"],
                                ].map(([label, value]) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                        <Text type="secondary">{label}</Text>
                                        <Text strong>{value}</Text>
                                    </div>
                                ))}
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                    <Text type="secondary">Status</Text>
                                    <Text strong style={{ color: summary.is_active === false ? "#f5222d" : "#52c41a" }}>
                                        {summary.is_active === false ? "Inactive" : "Active"}
                                    </Text>
                                </div>
                            </Space>
                        </Card>

                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}><BulbOutlined /> Helpful Tips</span>}
                            size="small"
                        >
                            <ul style={{ paddingLeft: 18, margin: 0, fontSize: 13, color: "#555", lineHeight: 1.9 }}>
                                <li>Use a unique SKU / Barcode for each item.</li>
                                <li>Set minimum stock and reorder level to avoid stock-outs.</li>
                                <li>Enable batch and expiry tracking if required.</li>
                                <li>Upload clear product images.</li>
                                <li>Add HSN/SAC code for accurate taxation.</li>
                            </ul>
                        </Card>

                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}><LinkOutlined /> Quick Links</span>}
                            size="small"
                        >
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {quickLinks.map(({ label, to }) => (
                                    <div
                                        key={label}
                                        onClick={() => navigate(to)}
                                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", fontSize: 13, color: "rgba(14, 93, 146, 0.95)" }}
                                    >
                                        <span>{label}</span>
                                        <span>›</span>
                                    </div>
                                ))}
                            </Space>
                        </Card>
                    </Col>
                </Row>
            </Card>
        </>
    );
};

export default AddItem;